/**
 * WP7b / #46616 — constrained verification stage overlay.
 *
 * Lives in the main column under the top bar (STAGE_OVERLAY_PLACEMENT).
 * Never a full-viewport scrim: review actions in the page header stay
 * clickable. Operator gated; non-operator events are ignored.
 */

import { useEffect, useState, type ReactNode } from "react";
import { Icon } from "./icons";
import { ActionButton } from "./ui";
import {
  CLOSE_STAGE_EVENT,
  OPEN_STAGE_EVENT,
  STAGE_OVERLAY_PLACEMENT,
  dispatchCloseStage,
  type TerminalAccess,
} from "./terminal-stage";

export function StageOverlay({
  access,
}: {
  access: TerminalAccess;
}): ReactNode {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!access.allowed) {
      setOpen(false);
      return;
    }
    const onOpen = () => setOpen(true);
    const onClose = () => setOpen(false);
    window.addEventListener(OPEN_STAGE_EVENT, onOpen);
    window.addEventListener(CLOSE_STAGE_EVENT, onClose);
    return () => {
      window.removeEventListener(OPEN_STAGE_EVENT, onOpen);
      window.removeEventListener(CLOSE_STAGE_EVENT, onClose);
    };
  }, [access.allowed]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      dispatchCloseStage();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!access.allowed || !open) return null;

  return (
    <div
      className="vp-stage-overlay"
      data-verification-stage=""
      data-stage-open="true"
      data-obscures-review={STAGE_OVERLAY_PLACEMENT.coversReviewActions ? "true" : "false"}
      data-stage-region={STAGE_OVERLAY_PLACEMENT.region}
      role="region"
      aria-label="Stage"
    >
      <div className="vp-stage-overlay-head">
        <span className="vp-stage-overlay-title">
          <Icon name="cpu" size={14} />
          Stage
        </span>
        <ActionButton actionId="shell.close-stage" variant="ghost" onClick={() => dispatchCloseStage()}>
          Close
        </ActionButton>
      </div>
      <div className="vp-stage-overlay-body">
        Operator stage. Review actions in the top bar stay reachable.
      </div>
    </div>
  );
}
